import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { getInitials, formatTimeAgo } from '../lib/utils';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Search, Loader2, UserPlus, UserMinus, FileText, Heart, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const { token, language } = useAuth();
  const tr = t(language);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState('all');
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initial search if query param exists
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q.length >= 2) {
      setQuery(q);
      handleSearch(q);
    }
  }, []);

  const handleSearch = useCallback(async (value) => {
    if (value.length < 2) {
      setUsers([]);
      setPosts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(value)}&limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleInputChange = (value) => {
    setQuery(value);
    setSearchParams(value ? { q: value } : {});
    handleSearch(value);
  };

  const handleFollow = async (userId, isFollowing) => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${userId}/follow`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_following: !isFollowing } : u
      ));
    } catch (error) {
      toast.error(tr('error'));
    }
  };

  const filteredUsers = activeTab === 'all' || activeTab === 'users' ? users : [];
  const filteredPosts = activeTab === 'all' || activeTab === 'posts' ? posts : [];

  return (
    <div className="max-w-2xl mx-auto" data-testid="search-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 p-4">
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={tr('searchUsers') || 'Search users, posts...'}
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            className="pl-12 h-12 rounded-full bg-secondary/50"
            data-testid="search-input"
          />
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          {['all', 'users', 'posts'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              size="sm"
              className="rounded-full capitalize"
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'all' ? 'All' : tab === 'users' ? 'Users' : 'Posts'}
            </Button>
          ))}
        </div>
      </header>

      {/* Results */}
      <div className="divide-y divide-border/40">
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {!loading && query.length >= 2 && filteredUsers.length === 0 && filteredPosts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <Search className="w-16 h-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">{tr('noResults') || 'No results found'}</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Try a different search term</p>
          </div>
        )}

        {/* Users Section */}
        {filteredUsers.length > 0 && (
          <div>
            <div className="px-4 py-3 bg-secondary/30">
              <h2 className="font-semibold flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Users ({filteredUsers.length})
              </h2>
            </div>
            {filteredUsers.map(user => (
              <div
                key={user.id}
                className="flex items-center gap-3 p-4 hover:bg-secondary/30 transition-colors"
                data-testid={`search-result-${user.id}`}
              >
                <Link to={`/profile/${user.username}`}>
                  <Avatar className="w-14 h-14 ring-2 ring-primary/10">
                    <AvatarImage src={user.avatar} alt={user.display_name} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(user.display_name || user.username)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                
                <Link to={`/profile/${user.username}`} className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{user.display_name || user.username}</p>
                  <p className="text-sm text-muted-foreground truncate">@{user.username}</p>
                </Link>

                <Button
                  variant={user.is_following ? "outline" : "default"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => handleFollow(user.id, user.is_following)}
                  data-testid={`follow-btn-${user.id}`}
                >
                  {user.is_following ? (
                    <>
                      <UserMinus className="w-4 h-4 mr-1" />
                      {tr('unfollow') || 'Unfollow'}
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-1" />
                      {tr('follow') || 'Follow'}
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Posts Section */}
        {filteredPosts.length > 0 && (
          <div>
            <div className="px-4 py-3 bg-secondary/30">
              <h2 className="font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Posts ({filteredPosts.length})
              </h2>
            </div>
            {filteredPosts.map(post => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="flex items-start gap-3 p-4 hover:bg-secondary/30 transition-colors"
                data-testid={`search-post-${post.id}`}
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={post.avatar} alt={post.username} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(post.display_name || post.username)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold truncate">{post.display_name || post.username}</p>
                    <p className="text-sm text-muted-foreground">@{post.username}</p>
                    <span className="text-muted-foreground">·</span>
                    <p className="text-sm text-muted-foreground">{formatTimeAgo(post.created_at, language)}</p>
                  </div>
                  <p className="text-sm line-clamp-2">{post.content}</p>
                  <div className="flex items-center gap-4 mt-2 text-muted-foreground text-sm">
                    <span className="flex items-center gap-1">
                      <Heart className={`w-4 h-4 ${post.is_liked ? 'fill-pink-500 text-pink-500' : ''}`} />
                      {post.likes_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {post.comments_count}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
